import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material'
import ButtonBase from 'design-system/button'

type ModalStep = 'watch_return' | 'finalize'

interface ClinicalTrialModalProps {
  open: boolean
  onClose: () => void
  currentStatus: 'active' | 'pending_watch_return'
  onWatchNotReturned: () => Promise<void>
  onReactivateStudy: () => Promise<void>
  onProceedToFinalize: (status: 'success' | 'discarded') => Promise<void>
  loading: boolean
}

const ClinicalTrialModal: React.FC<ClinicalTrialModalProps> = ({
  open,
  onClose,
  currentStatus,
  onWatchNotReturned,
  onReactivateStudy,
  onProceedToFinalize,
  loading
}) => {
  const [step, setStep] = useState<ModalStep>('watch_return')
  const [selectedWatchReturn, setSelectedWatchReturn] = useState<
    'not_returned' | 'returned' | 'reactivate' | null
  >(null)
  const [finalizeStatus, setFinalizeStatus] = useState<'success' | 'discarded'>(
    'success'
  )

  const handleClose = () => {
    setStep('watch_return')
    setSelectedWatchReturn(null)
    setFinalizeStatus('success')
    onClose()
  }

  const handleWatchReturnOption = async () => {
    if (!selectedWatchReturn) return

    if (selectedWatchReturn === 'not_returned') {
      await onWatchNotReturned()
      handleClose()
      return
    }

    if (selectedWatchReturn === 'reactivate') {
      await onReactivateStudy()
      handleClose()
      return
    }

    if (selectedWatchReturn === 'returned') {
      setStep('finalize')
      setSelectedWatchReturn(null)
    }
  }

  const handleFinalize = async () => {
    await onProceedToFinalize(finalizeStatus)
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      {step === 'watch_return' && (
        <>
          <DialogTitle>O paciente devolveu o relógio?</DialogTitle>
          <DialogContent>
            <RadioGroup
              value={selectedWatchReturn || ''}
              onChange={e => setSelectedWatchReturn(e.target.value as any)}
              sx={{ alignItems: 'flex-start', pt: 2 }}
            >
              <FormControlLabel
                value='not_returned'
                control={<Radio color='primary' />}
                label='Não, ainda está com paciente'
              />
              <FormControlLabel
                value='returned'
                control={<Radio color='primary' />}
                label='Sim, foi devolvido'
              />
              {currentStatus === 'pending_watch_return' && (
                <FormControlLabel
                  value='reactivate'
                  control={<Radio color='primary' />}
                  label='Reativar estudo'
                />
              )}
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <ButtonBase
              variant='outlined'
              color='primary'
              disabled={loading}
              onClick={handleClose}
            >
              Cancelar
            </ButtonBase>
            <ButtonBase
              variant='contained'
              loading={loading}
              disabled={loading || !selectedWatchReturn}
              onClick={handleWatchReturnOption}
            >
              Continuar
            </ButtonBase>
          </DialogActions>
        </>
      )}

      {step === 'finalize' && (
        <>
          <DialogTitle>Finalizar estudo</DialogTitle>
          <DialogContent>
            <Typography variant='body1' gutterBottom sx={{ pt: 1, mb: 2 }}>
              Selecione como deseja finalizar o estudo:
            </Typography>
            <RadioGroup
              value={finalizeStatus}
              onChange={e =>
                setFinalizeStatus(e.target.value as 'success' | 'discarded')
              }
              sx={{ alignItems: 'flex-start' }}
            >
              <FormControlLabel
                value='success'
                control={<Radio color='primary' />}
                label='Concluir estudo'
              />
              <FormControlLabel
                value='discarded'
                control={<Radio color='primary' />}
                label='Descartar estudo'
              />
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <ButtonBase
              variant='outlined'
              color='primary'
              disabled={loading}
              onClick={handleClose}
            >
              Cancelar
            </ButtonBase>
            <ButtonBase
              variant='contained'
              loading={loading}
              disabled={loading}
              onClick={handleFinalize}
            >
              Salvar
            </ButtonBase>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default ClinicalTrialModal
